import { div } from '@edicom/common/math/div';
import { isValidNumber } from '@edicom/common/number/isValidNumber';
export { fileSize } from './fileSize';

/**
 * Modulo que encapsula los formateadores localizados de numeros.
 *
 * Documentación
 * https://v8.dev/features/intl-numberformat
 * https://github.com/tc39/proposal-unified-intl-numberformat
 * https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Intl/NumberFormat
 * https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Intl/NumberFormat/NumberFormat
 *
 */

/**
 * Objeto que almacena los metodos de formateo que se van generando por locale, bajo demanda
 */
const cache = {
  // @example
  // `en-US`: {
  //   'number': value => _format(value),
  //   'unit_liter': value => _format(value),
  // },
};

const MAXIMUM_FRACTION_DIGITS = 10;

/**
 * Devuelve el numero `value` formateado y localizado.
 * > Nota que respeta el numero de decimales de `value`, con un maximo de 6 decimales. (y minimo 0)
 *
 * @param {Object} state
 * @param {number} value el numero a formatear
 * @param {Object} [params] opciones
 * @param {number} [params.decimal] el numero minimo de decimales a mostrar. por defecto, respeta las decimales del `value` hasta 10 maximo.
 * @param {number} [params.maxDecimal] el numero maximon de decimales a mostrar
 * @returns {string}
 *
 * style:
 * "decimal" for plain number formatting.
 * "currency" for currency formatting.
 * "percent" for percent formatting
 * "unit" for unit formatting
 */
export function number(state, value, params = {}) {
  if (!isValidNumber(value)) {
    return '';
  }
  try {
    // creamos la función si no existe para la locale
    cache[state.id] = cache[state.id] || {};

    const minDecimals = isValidInteger(params.decimal) ? params.decimal : undefined;
    const maxDecimals = isValidInteger(params.maxDecimal) ? params.maxDecimal : MAXIMUM_FRACTION_DIGITS;
    const key = `decimal_${minDecimals}-${maxDecimals}`;
    const options = { minimumFractionDigits: minDecimals, maximumFractionDigits: maxDecimals };

    if (!cache[state.id][key]) {
      cache[state.id][key] = new Intl.NumberFormat(state.id, options).format;
    }
    return cache[state.id][key](value);
  } catch (e) {
    console.error(e);
  }

  return '';
}

/**
 * Devuelve el numero `value` formateado y localizado en percentaje con el numero exacto de decimales requerido si definido.
 *
 * @param {Object} state
 * @param {number} value el numero a formatear
 * @param {Object} [params] opciones
 * @param {number} [params.decimal] el numero minimo de decimales a mostrar. por defecto, respeta las decimales del `value` hasta 10 maximo.
 * @param {number} [params.maxDecimal] el numero maximo de decimales a mostrar.
 * @returns {string}
 */
export function percent(state, value, params = {}) {
  if (!isValidNumber(value)) {
    return '';
  }
  try {
    const percentageValue = div(value, 100);

    const minDecimals = isValidInteger(params.decimal) ? params.decimal : undefined;
    const maxDecimals = isValidInteger(params.maxDecimal) ? params.maxDecimal : MAXIMUM_FRACTION_DIGITS;
    const key = `percent_${minDecimals}-${maxDecimals}`;
    const options = { style: 'percent', minimumFractionDigits: minDecimals, maximumFractionDigits: maxDecimals };

    // creamos la función si no existe para la locale
    cache[state.id] = cache[state.id] || {};
    if (!cache[state.id][key]) {
      cache[state.id][key] = new Intl.NumberFormat(state.id, options).format;
    }

    return cache[state.id][key](percentageValue);
  } catch (e) {
    console.error(e);
  }

  return '';
}

function isValidInteger(value) {
  return `${parseInt(value)}` === `${value}`;
}

/**
 * Devuelve el numero `value` formateado y localizado en modo compacto, (usando "K" para miles, "M",...)
 * @param {Object} state
 * @param {number} value el numero a formatear
 * @returns {string}
 *
 * @example
 * const formatter = new Intl.NumberFormat('en', {
 *   notation: 'compact',
 * });
 * formatter.format(1234.56);
 * // → '1.2K'
 * formatter.format(123456);
 * // → '123K'
 * formatter.format(123456789);
 * // → '123M'
 */
export function compact(state, value) {
  if (!isValidNumber(value)) {
    return '';
  }
  try {
    // creamos la función si no existe para la locale
    cache[state.id] = cache[state.id] || {};
    if (!cache[state.id]['compact']) {
      cache[state.id]['compact'] = valueToFormat =>
        valueToFormat.toLocaleString(state.id, {
          notation: 'compact',
          // compactDisplay: "short|long", default: "short"
        });
    }
    return cache[state.id]['compact'](value);
  } catch (e) {
    console.error(e);
  }

  return '';
}

/**
 * Devuelve el numero `value` formateado y localizado con la divisa
 *
 * @link [lista completa divisas](https://www.six-group.com/en/products-services/financial-information/data-standards.html#scrollTo=maintenance-agency)
 * @param {Object} state
 * @param {number} value el numero a formatear
 * @param {Object} [params] parametros opcionales
 * @param {string} [params.currency] Tipo de divisa ISO 4217, ej 'USD', 'EUR', ...
 * @param {number} [params.decimal] el numero de minimo de decimales, defecto, "no currency": 2 minimos, "si currency": 2 fijos, dependiendo del idioma
 * @param {number} [params.maxDecimal] el numero de maximo de decimales
 * @returns {string}
 */
export function currency(state, value, params = {}) {
  if (!isValidNumber(value)) {
    return '';
  }
  try {
    // creamos la función si no existe para la locale
    cache[state.id] = cache[state.id] || {};
    if (!params.currency) {
      return number(state, value, params);
    }

    const minDecimals = isValidInteger(params.decimal) ? params.decimal : undefined;
    const maxDecimals = isValidInteger(params.maxDecimal) ? params.maxDecimal : undefined;
    const key = `currency_${params.currency}_${minDecimals}-${maxDecimals}`;
    const options = { style: 'currency', currency: params.currency, minimumFractionDigits: minDecimals, maximumFractionDigits: maxDecimals };

    if (!cache[state.id][key]) {
      cache[state.id][key] = new Intl.NumberFormat(state.id, options).format;
    }
    return cache[state.id][key](value);
  } catch (e) {
    console.error(e);
  }

  return '';
}

/**
 * Devuelve el numero `value` formateado y localizado con la unidad
 * @param {Object} state
 * @param {string} name Tipo de unidad, ver lista de valores* que se pueden combinar**
 * @param {number} value el numero a formatear
 * @param {string} [params.unit] Tipo de unidad*
 * @param {number} [params.decimal] el numero de decimales minimos, defecto, "no currency": 2 minimos, "si currency": 2 fijos, dependiendo del idioma
 * @param {number} [params.maxDecimal] el numero de decimales maximos
 * @returns {string}
 *
 * *lista de valores posibles para `unit`:
 * - angle: degree
 * - area: acre, hectare
 * - concentration: percent
 * - digital: bit, byte, kilobit, kilobyte, megabit, megabyte, gigabit, gigabyte, terabit, terabyte, petabyte
 * - duration: millisecond, second, minute, hour, day, week, month, year
 * - length: millimeter, centimeter, meter, kilometer, inch, foot, yard, mile, mile-scandinavian
 * - mass: gram, kilogram, ounce, pound, stone
 * - temperature: celsius, fahrenheit
 * - volume: liter, milliliter, gallon, fluid-ounce
 *
 * **se pueden combinar las unidades usando "per":
 * ej. “liter-per-acre” or “meter-per-second”
 */
export function unit(state, value, params) {
  if (!isValidNumber(value)) {
    return '';
  }
  try {
    // creamos la función si no existe para la locale
    cache[state.id] = cache[state.id] || {};

    const minDecimals = isValidInteger(params.decimal) ? params.decimal : undefined;
    const maxDecimals = isValidInteger(params.maxDecimal) ? params.maxDecimal : undefined;

    const key = `unit_${params.unit}${minDecimals}-${maxDecimals}`;
    const options = { style: 'unit', unit: params.unit, minimumFractionDigits: minDecimals, maximumFractionDigits: maxDecimals };

    if (!cache[state.id][key]) {
      cache[state.id][key] = new Intl.NumberFormat(state.id, options).format;
    }

    return cache[state.id][key](value);
  } catch (e) {
    console.error(e);
  }

  return '';
}
